import type { Document } from "../document/Document";
import type { Layer, RasterLayer } from "../document/Layer";
import { PixelBuffer } from "../pixel/PixelBuffer";
import { compositeLayer } from "../pixel/blend";
import type { Command } from "./Command";

// Stamp Visible (Photoshop: Ctrl+Shift+Alt+E).
//
// Identical to "Merge Visible" except the source layers are NOT removed.
// Adds a single flattened raster layer at the top of the tree, leaving the
// rest of the tree intact. This is what PS calls "Stamp Visible" — useful
// for compositing while retaining live layers.

const STAMPED_ID = "stamped-visible";

export class StampVisibleCommand implements Command {
  readonly kind = "stamp-visible";

  apply(doc: Document): Document {
    const visible = collectVisibleRasters(doc.layers);
    if (visible.length === 0) return doc;

    const merged = new PixelBuffer(doc.width, doc.height);
    for (const r of visible) {
      const projected = projectOntoCanvas(merged.width, merged.height, r);
      compositeLayer(merged.data, projected, r.blendMode, r.opacity);
    }
    merged.markDirty();

    const newLayer: RasterLayer = {
      kind: "raster",
      id: uniqueId(doc.layers, STAMPED_ID),
      name: "Stamp Visible",
      visible: true,
      opacity: 1,
      fillOpacity: 1,
      blendMode: "normal",
      x: 0,
      y: 0,
      lockTransparency: false,
      lockImage: false,
      lockPosition: false,
      lockAll: false,
      clippedToBelow: false,
      pixels: merged,
    };

    return { ...doc, layers: [...doc.layers, newLayer], activeLayerId: newLayer.id };
  }

  invert(before: Document, _after: Document): Command {
    return new RestoreLayersCommand(before.layers, before.activeLayerId);
  }
}

class RestoreLayersCommand implements Command {
  readonly kind = "stamp-visible/restore";
  constructor(
    private readonly layers: readonly Layer[],
    private readonly activeLayerId: string | null,
  ) {}

  apply(doc: Document): Document {
    return { ...doc, layers: this.layers, activeLayerId: this.activeLayerId };
  }

  invert(_before: Document, _after: Document): Command {
    return new StampVisibleCommand();
  }
}

function collectVisibleRasters(layers: readonly Layer[]): RasterLayer[] {
  const out: RasterLayer[] = [];
  walk(layers, true, out);
  return out;
}

function walk(layers: readonly Layer[], parentVisible: boolean, acc: RasterLayer[]): void {
  for (const l of layers) {
    const v = parentVisible && l.visible;
    if (!v) continue;
    if (l.kind === "raster") acc.push(l);
    else if (l.kind === "group") walk(l.children, v, acc);
  }
}

function projectOntoCanvas(
  canvasW: number,
  canvasH: number,
  layer: RasterLayer,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(canvasW * canvasH * 4);
  const lw = layer.pixels.width;
  const lh = layer.pixels.height;
  const x0 = Math.max(0, layer.x);
  const y0 = Math.max(0, layer.y);
  const x1 = Math.min(canvasW, layer.x + lw);
  const y1 = Math.min(canvasH, layer.y + lh);
  for (let y = y0; y < y1; y++) {
    const srcRow = y - layer.y;
    for (let x = x0; x < x1; x++) {
      const srcCol = x - layer.x;
      const srcOff = (srcRow * lw + srcCol) * 4;
      const dstOff = (y * canvasW + x) * 4;
      out[dstOff] = layer.pixels.data[srcOff]!;
      out[dstOff + 1] = layer.pixels.data[srcOff + 1]!;
      out[dstOff + 2] = layer.pixels.data[srcOff + 2]!;
      out[dstOff + 3] = layer.pixels.data[srcOff + 3]!;
    }
  }
  return out;
}

function uniqueId(layers: readonly Layer[], base: string): string {
  const taken = new Set<string>();
  collectIds(layers, taken);
  if (!taken.has(base)) return base;
  let i = 1;
  while (taken.has(`${base}-${i}`)) i++;
  return `${base}-${i}`;
}

function collectIds(layers: readonly Layer[], out: Set<string>): void {
  for (const l of layers) {
    out.add(l.id);
    if (l.kind === "group") collectIds(l.children, out);
  }
}
